import metrics from "../../utils/metrics";
import { Popover, Transition } from "@headlessui/react";
import { BellIcon } from "@heroicons/react/24/outline";
import { classNames, isFrontend } from "~/utils";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePopper } from "react-popper";
import { graphql } from "~/graphql/codegen";
import {
  useQuery,
  useLazyQuery,
  useMutation,
  useSubscription,
} from "@apollo/client/react";
import type {
  Notification,
  GetAnnouncementsQuery,
  GetAnnouncementsQueryVariables,
  MarkAllNotificationAsReadMutation,
  MarkAllNotificationAsReadMutationVariables,
  MarkNotificationAsReadMutation,
  MarkNotificationAsReadMutationVariables,
  NotificationAddedSubscription,
  NotificationAddedSubscriptionVariables,
  NotificationsCountQuery,
  NotificationsCountQueryVariables,
  NotificationsQuery,
  NotificationsQueryVariables,
  ReadAnnouncementsMutation,
  ReadAnnouncementsMutationVariables,
} from "~/graphql/codegen/graphql";

const GET_ANNOUNCEMENTS = graphql(`
  query GetAnnouncements {
    getAnnouncements {
      hasNew
      url
    }
  }
`);

const MARK_ALL_NOTIFICATIONS_AS_READ = graphql(`
  mutation markAllNotificationAsRead {
    markAllNotificationsAsRead
  }
`);

const MARK_NOTIFICATION_AS_READ = graphql(`
  mutation markNotificationAsRead($id: ID!) {
    markNotificationAsRead(id: $id)
  }
`);

const NOTIFICATION_ADDED = graphql(`
  subscription NotificationAdded {
    notificationAdded {
      id
      read
      createdAt
      icon
      actionURL
      body
      title
    }
  }
`);

const NOTIFICATIONS = graphql(`
  query Notifications($from: String) {
    notifications(from: $from) {
      items {
        id
        read
        createdAt
        icon
        actionURL
        body
        title
      }
      nextItem
      unreadCount
    }
  }
`);

const NOTIFICATIONS_COUNT = graphql(`
  query NotificationsCount {
    notifications(from: "", size: 1) {
      items {
        id
      }
      unreadCount
    }
    getAnnouncements {
      hasNew
      url
    }
  }
`);

const READ_ANNOUNCEMENTS = graphql(`
  mutation readAnnouncements {
    readAnnouncements
  }
`);
import { Link } from "react-router";
import NotificationContent from "./notification-content";
import { useIsAuthenticated, useUser } from "~/hooks/context-hook";
import {
  Badge,
  Button,
  cn,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@mindstonehq/ui";
import useInfiniteScroll from "~/hooks/infinite-scroll";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function Notifications({
  container,
  iconClassName,
}: {
  container?: HTMLDivElement;
  iconClassName?: string;
}) {
  const isAuthenticated = useIsAuthenticated();
  const isClient = isFrontend();
  const { data, refetch } = useQuery<
    NotificationsCountQuery,
    NotificationsCountQueryVariables
  >(NOTIFICATIONS_COUNT, {
    skip: !isAuthenticated,
  });

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (isClient) {
      setUnreadCount(
        (data?.notifications?.unreadCount || 0) +
          (data?.getAnnouncements?.hasNew ? 1 : 0),
      );
    }
  }, [data, isClient]);

  useSubscription<
    NotificationAddedSubscription,
    NotificationAddedSubscriptionVariables
  >(NOTIFICATION_ADDED, {
    skip: !isAuthenticated,
    onData: (d) => {
      if (!d.data?.data?.notificationAdded) {
        return;
      }
      // add the new notification to the grouped notifications
      setUnreadCount((prev) => prev + 1);
    },
  });

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={"relative"}
          onClick={() => metrics.track("notification_panel_opened")}
        >
          <span className="sr-only"><Trans>Open notifications</Trans></span>
          <div className="relative">
            <BellIcon className={cn("h-5 w-5", iconClassName)} />
            {isClient && !!unreadCount && (
              <span className="absolute -top-0.5 -right-0.5 flex h-[6px] w-[6px] rounded-full bg-primary" />
            )}
          </div>
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col h-full">
        <NotificationSheetContent updateCount={() => refetch()} />
      </SheetContent>
    </Sheet>
  );
}

function NotificationSheetContent({ updateCount }: { updateCount: () => any }) {
  const { t } = useLingui();
  const markedAsSeen = useRef<{ [key: string]: boolean }>({});
  const { data } = useQuery<NotificationsQuery, NotificationsQueryVariables>(
    NOTIFICATIONS,
    {
      variables: {
        from: "",
      },
      fetchPolicy: "no-cache",
    },
  );
  const [fetchNotifications] = useLazyQuery<
    NotificationsQuery,
    NotificationsQueryVariables
  >(NOTIFICATIONS);
  const [groupedNotifications, setGroupedNotifications] = useState(
    {} as {
      [key: string]: Notification[];
    },
  );
  const { data: announcements, refetch: refetchAnnouncements } = useQuery<
    GetAnnouncementsQuery,
    GetAnnouncementsQueryVariables
  >(GET_ANNOUNCEMENTS);
  const [readAnnouncements] = useMutation<
    ReadAnnouncementsMutation,
    ReadAnnouncementsMutationVariables
  >(READ_ANNOUNCEMENTS);
  const [markNotificationAsRead] = useMutation<
    MarkNotificationAsReadMutation,
    MarkNotificationAsReadMutationVariables
  >(MARK_NOTIFICATION_AS_READ);
  const [markAllNotificationsAsRead] = useMutation<
    MarkAllNotificationAsReadMutation,
    MarkAllNotificationAsReadMutationVariables
  >(MARK_ALL_NOTIFICATIONS_AS_READ);
  const isAuthenticated = useIsAuthenticated();

  const [initialData, setInitialData] = useState({
    items: data?.notifications?.items || [],
    hasMore: !!data?.notifications?.nextItem,
    nextItem: data?.notifications?.nextItem || "",
  });
  useEffect(() => {
    if (!data) {
      return;
    }

    setInitialData({
      items: data?.notifications?.items || [],
      hasMore: !!data?.notifications?.nextItem,
      nextItem: data?.notifications?.nextItem || "",
    });
  }, [data]);

  const { items, hasMore, sentinelRef, nextItem, setItems } = useInfiniteScroll(
    initialData,
    (offset, _signal) =>
      fetchNotifications({
        variables: {
          from: offset,
        },
      }).then((res) => ({
        items: res?.data?.notifications?.items || [],
        hasMore: !!res?.data?.notifications?.nextItem,
        nextItem: res?.data?.notifications?.nextItem,
      })),
  );

  useSubscription<
    NotificationAddedSubscription,
    NotificationAddedSubscriptionVariables
  >(NOTIFICATION_ADDED, {
    skip: !isAuthenticated,
    onData: (d) => {
      if (d.data.error) {
        console.error("websocket error: ", d.data.error);
      }
      const updatedNotification = d.data.data?.notificationAdded;

      if (updatedNotification) {
        // add the new notification to the grouped notifications
        setItems((prev) => [updatedNotification, ...prev]);
      } else {
        console.error("evidencing error", d.data.error);
      }
    },
  });

  useEffect(() => {
    updateGroupedNotifications(items);
  }, [items]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.target) return;

          if (entry.isIntersecting) {
            const read = entry.target.getAttribute?.("data-read");
            if (read === "true") return;

            const id = entry.target.id;
            if (!id) return;
            handleMarkAsRead(id);
          }
        });
      },
      { threshold: 1.0 },
    );

    Object.values(groupedNotifications).forEach((group) => {
      group.forEach((notification) => {
        const element = document.getElementById(notification.id);
        if (element) {
          observer.observe(element);
        }
      });
    });

    return () => {
      observer.disconnect();
    };
  }, [groupedNotifications]);

  const handleMarkAsRead = (id: string) => {
    if (!markedAsSeen?.current || markedAsSeen?.current[id]) return;
    markedAsSeen.current[id] = true;
    markNotificationAsRead({
      variables: {
        id,
      },
    }).then(() => {
      updateCount();
    });
  };

  const handleMarkAllAsRead = () => {
    metrics.track("notification_item_mark_all_read");
    markAllNotificationsAsRead({
      refetchQueries: [NOTIFICATIONS],
    }).finally(() => {
      updateCount();
    });
  };

  const updateGroupedNotifications = (notifications: Notification[]) => {
    const groupedNotifications: {
      [key: string]: Notification[];
    } = {};
    const groups: Record<string, string> = {
      today: t(msg`Today`),
      yesterday: t(msg`Yesterday`),
      thisWeek: t(msg`This week`),
      lastWeek: t(msg`Last week`),
      older: t(msg`Older`),
    };
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const lastWeekStart = new Date();
    lastWeekStart.setDate(lastWeekStart.getDate() - lastWeekStart.getDay() - 7);
    const lastWeekEnd = new Date();
    lastWeekEnd.setDate(lastWeekEnd.getDate() - lastWeekEnd.getDay() - 1);

    notifications.forEach((notification) => {
      const date = new Date(notification.createdAt);
      if (date.toLocaleDateString() === new Date().toLocaleDateString()) {
        if (!groupedNotifications[groups.today]) {
          groupedNotifications[groups.today] = [];
        }
        groupedNotifications[groups.today].push(notification);
        return;
      }
      if (date.toLocaleDateString() === yesterday.toLocaleDateString()) {
        if (!groupedNotifications[groups.yesterday]) {
          groupedNotifications[groups.yesterday] = [];
        }
        groupedNotifications[groups.yesterday].push(notification);
        return;
      }
      if (date >= weekStart) {
        if (!groupedNotifications[groups.thisWeek]) {
          groupedNotifications[groups.thisWeek] = [];
        }
        groupedNotifications[groups.thisWeek].push(notification);
        return;
      }
      if (date >= lastWeekStart && date <= lastWeekEnd) {
        if (!groupedNotifications[groups.lastWeek]) {
          groupedNotifications[groups.lastWeek] = [];
        }
        groupedNotifications[groups.lastWeek].push(notification);
        return;
      }

      if (!groupedNotifications[groups.older]) {
        groupedNotifications[groups.older] = [];
      }
      groupedNotifications[groups.older].push(notification);
    });
    setGroupedNotifications(groupedNotifications);
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-2xl"><Trans>Notifications</Trans></SheetTitle>
        <SheetDescription>
          <button
            className="text-base font-medium link"
            onClick={handleMarkAllAsRead}
          >
            <Trans>Mark all as read</Trans>
          </button>
        </SheetDescription>
      </SheetHeader>
      <div className="flex flex-1 flex-col overflow-y-auto p-6 -mr-6">
        <a
          href={
            announcements?.getAnnouncements?.url || "https://news.mindstone.com"
          }
          target="_blank"
          className="flex items-center pb-3 cursor-pointer"
          onClick={() => {
            fetch(`/internal/read-announcements`, {
              keepalive: true,
              method: "POST",
              headers: {
                "content-type": "application/json",
              },
            });
          }}
        >
          <div
            className={classNames(
              "h-2 w-2 rounded-full",
              announcements?.getAnnouncements?.hasNew ? "bg-destructive" : "",
            )}
          ></div>
          <div className="ml-2 flex flex-col flex-1 py-2">
            <div className="text-base font-bold"><Trans>📢 Mindstone news</Trans></div>
            <div className="text-sm">
              <Trans>New features, improvements and product updates</Trans>
            </div>
          </div>
        </a>
        <div className="border-b border-border mx-6"></div>
        <div className="py-3 flex flex-col">
          {Object.keys(groupedNotifications).map((key, i) => {
            const notifications = groupedNotifications[key];
            return (
              <React.Fragment key={key}>
                <div className="flex flex-col py-3">
                  <div className="font-semibold">{key}</div>
                  <>
                    {notifications.map((notification) => (
                      <div key={notification.id}>
                        {notification.actionURL ? (
                          <Link
                            to={notification.actionURL}
                            className="cursor-pointer"
                            onClick={() =>
                              metrics.track("notification_item_clicked", {
                                notification_id: notification.id,
                                notification_title: notification.title,
                              })
                            }
                          >
                            <NotificationContent notification={notification} />
                          </Link>
                        ) : (
                          <div
                            className="cursor-pointer"
                            onClick={() =>
                              metrics.track("notification_item_clicked", {
                                notification_id: notification.id,
                                notification_title: notification.title,
                              })
                            }
                          >
                            <NotificationContent notification={notification} />
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                </div>
                {i !== Object.keys(groupedNotifications).length - 1 && (
                  <div className="border-b border-border mx-12"></div>
                )}
              </React.Fragment>
            );
          })}
          <div ref={sentinelRef} className="h-1 pb-1" />
          {!Object.keys(groupedNotifications)?.length && (
            <div className="flex flex-col px-12 items-center pt-16">
              <div className="text-lg font-bold"><Trans>All caught up!</Trans></div>
              <div className="text-sm">
                <Trans>All your important notifications will appear here.</Trans>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
